import React from 'react'
import { SurveyQuestionType } from '../../../../models/Survey'
import { type ReportWithComments, type Report, type KeyStatSurveyItem, type ReportsV2, type IReportSurveySection } from '../../../../models/Report'
import { type SurveyCompletionRate } from '../../../../models/Division'
import { countNonDescriptiveQuestions, titleFormatted } from '../../../../helpers'
import SurveySectionAccordion from './surveySectionAccordion'
import SurveyItemResponsePlaceHolder from '../SurveyItemResponsePlaceHolder'
import CommentCard from '../CommentCard'
import SurveyReportChart from './SurveyReportChart'
import { useReportsContext } from '../../ReportsContext'
import { type SurveyResponseMenuSelection } from '../Charts/BarChart'
import { isCommentsExportAvailable } from '../helper'

interface Props {
  reports: ReportsV2
  originalReports?: ReportsV2 | null
  completionRate: SurveyCompletionRate
  reportError: Record<string, any> | null
  expandedSections: Record<number, boolean>
  onExpandedSectionsChange: React.Dispatch<React.SetStateAction<Record<number, boolean>>>
  onFilterBySurveyResponse?: (selection: SurveyResponseMenuSelection) => void | Promise<void>
  onRemoveResponseFilterAnswer?: (answer: string) => void | Promise<void>
  responseFilterOverrideSurveyItem?: KeyStatSurveyItem | null
  responseFilterHighlightedAnswers?: string[]
  isResponseFilterApplied?: boolean
  responseFilterWarningMessage?: string | null
  onRequestCommentsExport?: (questionScope: { surveyItemId: string, revisionId: number }) => void
  disableFlaggedCommentActions?: boolean
}

const SurveyReportItems: React.FC<Props> = ({
  reports,
  originalReports = null,
  completionRate,
  reportError,
  expandedSections,
  onExpandedSectionsChange,
  onFilterBySurveyResponse,
  onRemoveResponseFilterAnswer,
  responseFilterOverrideSurveyItem = null,
  responseFilterHighlightedAnswers = [],
  isResponseFilterApplied = false,
  responseFilterWarningMessage = null,
  onRequestCommentsExport,
  disableFlaggedCommentActions = false
}) => {
  const { selectedTrend, displaySettings } = useReportsContext()

  const isGenericAcademicUnit = completionRate && 'divisionId' in completionRate
  const showCommentsExportPrompt = isGenericAcademicUnit && isCommentsExportAvailable(displaySettings, selectedTrend)

  const renderFreeResponse = (report: ReportWithComments, questionNumber: number): JSX.Element => {
    const titleText = `${questionNumber}. ${report.prompt}`
    if ('releaseDate' in report || isGenericAcademicUnit) {
      return (
        <SurveyItemResponsePlaceHolder
          key={questionNumber}
          title={titleText}
          releaseTimeStamp={report?.releaseDate}
          useCommentsExportMessage={showCommentsExportPrompt && report?.releaseDate == null}
          onCommentsExportClick={() => {
            onRequestCommentsExport?.({
              surveyItemId: report.itemId,
              revisionId: report.revision
            })
          }}
        />
      )
    }
    return <CommentCard key={questionNumber} title={titleText} report={report} titles={reports?.titles} disableFlaggedCommentActions={disableFlaggedCommentActions} />
  }

  const showSlice = displaySettings?.slices?.length > 1 && displaySettings?.comparisons?.[0].comparison === null
  const originalReportItemsById = React.useMemo(() => {
    const sections = originalReports?.surveySections ?? []
    const entries = sections.flatMap((section) => {
      const surveyItems = Array.isArray(section.surveyItems) ? section.surveyItems : [section.surveyItems]
      return surveyItems
        .filter((item): item is KeyStatSurveyItem => Boolean(item?.itemId))
        .map((item) => [item.itemId, item] as const)
    })

    return new Map(entries)
  }, [originalReports?.surveySections])

  const renderBarChart = (report: any, questionNumber: number, section: IReportSurveySection): JSX.Element => {
    const isResponseFilterOverrideTarget = responseFilterOverrideSurveyItem?.itemId === report.itemId
    const isActiveResponseFilterTarget = displaySettings?.responseFilter?.itemId === report.itemId
    const effectiveReport = isResponseFilterOverrideTarget ? responseFilterOverrideSurveyItem : report
    const currentCalculations = report?.calculations ?? effectiveReport?.calculations ?? []
    const originalReportItem = originalReportItemsById.get(report.itemId)
    const comparisonCalculations = !isResponseFilterApplied
      ? null
      : isResponseFilterOverrideTarget
        ? responseFilterOverrideSurveyItem?.calculations ?? []
        : originalReportItem?.calculations ?? null

    return (
      <SurveyReportChart
        report={effectiveReport}
        surveyItem={report}
        reports={reports}
        calculations={currentCalculations}
        comparisonCalculations={comparisonCalculations}
        selectedTrend={selectedTrend}
        showSlice={showSlice}
        questionNumber={questionNumber}
        sectionTitle={section.title ?? section.name ?? 'Survey Results'}
        surveySectionId={section.surveySectionId}
        surveySectionRevision={section.surveySectionRevision ?? section.revision}
        instructorId={section.instructorId || null}
        onFilterBySurveyResponse={onFilterBySurveyResponse}
        onRemoveResponseFilterAnswer={onRemoveResponseFilterAnswer}
        highlightedResponseAnswers={isActiveResponseFilterTarget ? responseFilterHighlightedAnswers : []}
        responseFilterWarningMessage={responseFilterWarningMessage}
      />
    )
  }

  const renderTitle = (report: Report): JSX.Element => titleFormatted(report.prompt)

  const handleReportItem = (report: Report, questionNumber: number, section: IReportSurveySection): JSX.Element => {
    switch (report.itemType) {
      case SurveyQuestionType.FreeResponse:
        return renderFreeResponse(report, questionNumber)
      case SurveyQuestionType.LinearScale:
      case SurveyQuestionType.MultipleChoice:
      case SurveyQuestionType.MultiSelect:
        return renderBarChart(report, questionNumber, section)
      case SurveyQuestionType.Title:
        return renderTitle(report)
      default:
        return <></>
    }
  }

  const renderItems = (surveyItems: any[], section: IReportSurveySection): JSX.Element => {
    let questionNumber = 1
    return (
      <>
        {surveyItems?.map((item: any) => {
          const rendered = handleReportItem(item, questionNumber, section)
          if (item.itemType !== SurveyQuestionType.Title) {
            questionNumber += 1
          }
          return <React.Fragment key={item.itemId ?? item.prompt}>{rendered}</React.Fragment>
        })}
      </>
    )
  }

  return (
    <SurveySectionAccordion
      surveySections={reports.surveySections}
      countNonDescriptiveQuestions={countNonDescriptiveQuestions}
      renderItems={renderItems}
      expandedSections={expandedSections}
      onExpandedSectionsChange={onExpandedSectionsChange}
    />
  )
}

export default SurveyReportItems
